/**
 * catalog-requests/form — 폼 ↔ 초안의 순수 함수(VOCABULARY "요청함 페이지").
 * 폼은 글만 쥔다. 보낼 때 data_type(pg 이름)으로 숫자 · 불 · JSON 을 만든다 — 변환 규칙은 여기 한 곳.
 */
import { E_CATALOG_TABLES, E_PART_NUMBER_COLUMNS } from '../../api/client';
import type { CatalogDraft, CatalogNumberMatch, CatalogRequestView, DraftMatchView, DraftNumbers, ECatalogTable, ModelColumnsView, ModelRowView, TableColumnView } from '../../api/client';
import { assert } from '../../lib/assert';
import { trimmedFillsOf } from '../../components/slot-reading';
import type { DraftForm } from './types';

/** pg data_type 중 숫자로 보낼 것 */
const NUMBER_TYPES: readonly string[] = ['integer', 'smallint', 'bigint', 'numeric', 'real', 'double precision'];
/** 그중 정수 — number 입력의 step 이 1 */
const INTEGER_TYPES: readonly string[] = ['integer', 'smallint', 'bigint'];
/** 지수 꼴 수 글(1e-7) — 자리 제한 열엔 안 들어간다(서버 EXPONENT_PATTERN 과 같다) */
const EXPONENT_PATTERN = /e/i;
/** 숫자 꼴을 볼 때 쓰는 열의 칸 — 부품 칸 · 모델 칸이 같은 판정을 쓴다 */
type NumberColumn = Pick<TableColumnView, 'data_type' | 'numeric_precision' | 'numeric_scale'>;
/** 입력 칸의 꼴 — data_type 을 넷으로 가른다(renderCellInput 이 그린다) */
export type ECellKind = 'integer' | 'number' | 'boolean' | 'json' | 'text';

/** 요청 한 건 → 폼. spec_draft 값은 String() (jsonb 값은 JSON 글), 표는 요청의 것(없으면 null — 사람이 고른다) */
export function formOf(view: CatalogRequestView): DraftForm {
  const values: Record<string, string> = {};
  for (const [key, value] of Object.entries(view.spec_draft)) {
    values[key] = cellTextOf(value);
  }
  const modelValues: Record<string, string> = {};
  for (const [key, value] of Object.entries(view.model_draft ?? {})) {
    modelValues[key] = cellTextOf(value);
  }
  return {
    option_part: view.option_part ?? '',
    table: tableOrNull(view.catalog_table_name),
    values,
    evidence: { ...view.spec_evidence },
    model_values: modelValues,
    description_fills: [],   // 서버에 안 남는다 — 승격 때 다시 채운다
  };
}

/** 서버가 만드는 칸 — 칸 표에 안 보인다. 약식은 원문을 규칙이 읽어 · 채운 자리는 약식 상자에서(2026-10-26 승격도 규칙이) */
export const SERVER_MADE_COLUMNS: readonly string[] = ['description', 'description_fills'];

/** 원문 칸의 글 — 규칙 읽기 · 승격 잠금이 본다 */
export function rawOf(form: DraftForm): string {
  return (form.values.description_raw ?? '').trim();
}

/** 근거의 모델 칸 키 — 'model.<열>'. 부품 칸과 한 evidence 에 산다 */
export const MODEL_EVIDENCE_PREFIX = 'model.';
export function modelEvidenceKeyOf(column: string): string {
  return `${MODEL_EVIDENCE_PREFIX}${column}`;
}

/** 폼 → 보낼 초안. '' 칸은 뺀다. table 이 null 이면 못 만든다 — 부르는 쪽이 먼저 막는다 */
export function draftOf(form: DraftForm, columns: readonly TableColumnView[], model: ModelColumnsView | null): CatalogDraft {
  assert(form.table !== null, '초안은 표가 정해진 뒤에만 만든다 — [저장] · [승격] 은 표가 null 이면 잠겨 있어야 했다');
  const evidence: Record<string, string> = {};
  const values = cellValuesOf(form.values, columns, form.evidence, evidence, '');
  const modelValues = model === null ? null : cellValuesOf(form.model_values, model.rows, form.evidence, evidence, MODEL_EVIDENCE_PREFIX);
  const pn = form.option_part.trim();
  return { option_part: pn === '' ? null : pn, catalog_table_name: form.table, columns: values, evidence, model_columns: modelValues, description_fills: trimmedFillsOf(form.description_fills) };
}

// ─── 승격 — 번호가 이미 있는 행이면 수정(2026-10-20) ───
/** 폼 → 번호 넷(옵션은 파트넘버 칸, 나머지 셋은 칸 표의 그 열). 빈 칸은 null */
export function numbersOf(form: DraftForm): DraftNumbers {
  const numbers: DraftNumbers = { option_part: null, spare_part: null, assembly_part: null, factory_part: null };
  for (const column of E_PART_NUMBER_COLUMNS) {
    const text = column === 'option_part' ? form.option_part : (form.values[column] ?? '');
    numbers[column] = text.trim() === '' ? null : text.trim();
  }
  return numbers;
}

/** 번호 칸인가 — 바뀌면 카탈로그를 다시 찾는다 */
export function isNumberColumn(name: string): boolean {
  return E_PART_NUMBER_COLUMNS.some((column) => column === name);
}

/** 옵션 PN 이 카탈로그에 있는 행 — 판정 edit 의 그 행, 또는 choose 중 옵션 PN 으로 맞은 행. 없으면 null(스페어 · 어셈블리만 맞았거나 새 행) */
export function optionPartMatchOrNull(match: DraftMatchView | null): CatalogNumberMatch | null {
  if (match === null) return null;
  const target = match.target;
  const byTarget = target === null ? undefined : match.matches.find((m) => m.table === target.table && m.row_id === target.row_id);
  if (byTarget !== undefined) return byTarget;
  const byOption = match.matches.find((m) => m.matched_column === 'option_part');
  return byOption === undefined ? null : byOption;
}

/** 글 칸 → 값(data_type 대로). '' 칸은 뺀다. 근거는 evidenceOut 에 모은다(키 = prefix + 열) */
function cellValuesOf(texts: Record<string, string>, columns: readonly TableColumnView[], evidenceIn: Record<string, string>, evidenceOut: Record<string, string>, prefix: string): Record<string, unknown> {
  const typeOf = new Map(columns.map((column) => [column.name, column.data_type]));
  const values: Record<string, unknown> = {};
  for (const [key, text] of Object.entries(texts)) {
    if (text.trim() === '') continue;
    const dataType = typeOf.get(key);
    if (dataType === undefined) continue;   // 지금 표에 없는 칸 — 카테고리를 바꿨다 되돌리면 옛 표의 칸이 남는다. 보내지 않는다(QA 2026-10-20 #1 승격 500)
    values[key] = cellValueOf(text.trim(), dataType);
    const url = evidenceIn[prefix + key];
    if (url !== undefined && url.trim() !== '') evidenceOut[prefix + key] = url.trim();
  }
  return values;
}

/** 근거만 적히고 값은 빈 칸 — 저장 때 근거가 버려진다. 라벨 목록(버튼 바에 안내, QA 2026-10-20 #17) */
export function orphanEvidenceLabelsOf(form: DraftForm, columns: readonly TableColumnView[], model: ModelColumnsView | null): string[] {
  const labels: string[] = [];
  for (const column of columns) {
    const url = form.evidence[column.name] ?? '';
    if (url.trim() !== '' && (form.values[column.name] ?? '').trim() === '') labels.push(columnLabelOf(column));
  }
  for (const column of model === null ? [] : model.rows) {
    const url = form.evidence[modelEvidenceKeyOf(column.name)] ?? '';
    if (url.trim() !== '' && (form.model_values[column.name] ?? '').trim() === '') labels.push(`모델 ${columnLabelOf(column)}`);
  }
  return labels;
}

/** 모델 칸의 빈 필수 열 — 모델이 **없을 때만**(새로 만든다). 있으면 행이 이미 필수를 가졌다. 열쇠 열은 부품 칸(key_column)이 채운다 */
export function missingRequiredModel(form: DraftForm, model: ModelColumnsView | null, modelRow: ModelRowView | null): string[] {
  if (model === null) return [];
  if (modelRow !== null && modelRow.row !== null) return [];
  return model.rows
    .filter((column) => column.required && column.name !== model.key_column && column.name !== 'vendor')   // vendor 는 부품의 oem_vendor 로 채워진다
    .filter((column) => (form.model_values[column.name] ?? '').trim() === '')
    .map((column) => column.name);
}

/** 비어 있는 필수 열 이름 — [승격] 잠금 · 빨간 표시. option_part · vendor 같은 열쇠 열도 columns 에 있다 */
export function missingRequired(form: DraftForm, columns: readonly TableColumnView[]): string[] {
  return columns
    .filter((column) => column.required && !SERVER_MADE_COLUMNS.includes(column.name))   // 약식은 서버가 만든다 — 원문 · 필수 자리는 약식 상자가 막는다
    .filter((column) => (column.name === 'option_part' ? form.option_part.trim() === '' : (form.values[column.name] ?? '').trim() === ''))
    .map((column) => column.name);
}

/** 숫자 칸 꼴이 틀린 열 이름 — 정수 칸에 소수 · 글('1U'), 소수 칸에 글, NUMERIC(p,s) 칸에 자리 넘침(0.035 → (4,2)). 서버도 400 으로 막는다(parts V장 "숫자 칸의 꼴" · "소수 자리 넘침"). [승격] 잠금 · 붉은 줄 */
export function wrongNumberColumns(form: DraftForm, columns: readonly TableColumnView[]): string[] {
  return columns
    .filter((column) => !SERVER_MADE_COLUMNS.includes(column.name))
    .filter((column) => !isNumberShape(column, (form.values[column.name] ?? '').trim()))
    .map((column) => column.name);
}

/** 모델 칸 중 숫자 꼴이 틀린 열 이름 — 서버는 모델 행에 실제로 쓰는 칸만 본다(새 행은 전부 · 있는 행은 빈 칸만, 2026-10-27). 모델 표가 없으면 [] */
export function wrongModelNumberColumns(form: DraftForm, model: ModelColumnsView | null, modelRow: ModelRowView | null): string[] {
  if (model === null) return [];
  const existing = modelRow === null || modelRow.row === null ? null : modelRow.row;
  return model.rows
    .filter((column) => !isFilledModelCell(existing, column.name))
    .filter((column) => !isNumberShape(column, (form.model_values[column.name] ?? '').trim()))
    .map((column) => column.name);
}

/** 있는 모델 행의 그 칸이 찼나 — 찬 칸은 승격이 안 쓴다(빈 칸만 채움) */
function isFilledModelCell(row: Record<string, unknown> | null, name: string): boolean {
  const value = row === null ? null : row[name];
  return value !== null && value !== undefined && String(value).trim() !== '';
}

/** 글이 그 칸의 숫자 꼴인가 — 빈 글 · 숫자 아닌 칸은 늘 참. NUMERIC(p,s) 면 소수 자리 ≤ s · 정수 자리 ≤ p − s(자리는 글로 센다) */
export function isNumberShape(column: NumberColumn, text: string): boolean {
  const kind = cellKindOf(column);
  if (text === '' || (kind !== 'integer' && kind !== 'number')) return true;
  const n = Number(text);
  if (kind === 'integer') return Number.isInteger(n);
  return Number.isFinite(n) && fitsNumeric(column, text);
}

/** 그 칸에 들어가는 꼴 한마디 — 붉은 줄 밑 안내 · 버튼 바 */
export function numberShapeWordOf(column: NumberColumn): string {
  if (cellKindOf(column) === 'integer') return '정수만';
  if (column.numeric_precision === null || column.numeric_scale === null) return '수만';
  return `소수 ${column.numeric_scale}자리 · 정수 ${column.numeric_precision - column.numeric_scale}자리까지`;
}

/** NUMERIC(p,s) 에 반올림 · 넘침 없이 들어가나 — 서버 fitsNumericOf 와 같은 규칙. 자리 제한 없는 열은 늘 참 */
function fitsNumeric(column: NumberColumn, text: string): boolean {
  if (column.numeric_precision === null || column.numeric_scale === null) return true;
  if (EXPONENT_PATTERN.test(text)) return false;
  const [integerPart = '', fractionPart = ''] = text.replace(/^[-+]/, '').split('.');
  const integerDigits = integerPart.replace(/^0+/, '').length;
  const fractionDigits = fractionPart.replace(/0+$/, '').length;
  return fractionDigits <= column.numeric_scale && integerDigits <= column.numeric_precision - column.numeric_scale;
}

/** 열 하나의 입력 꼴 — 정수 · 소수 · 예/아니오 · JSON · 글 */
export function cellKindOf(column: { data_type: string }): ECellKind {   // 열 꼴만 있으면 된다 — 카탈로그 행 드로어(CatalogRowColumn)도 쓴다
  if (INTEGER_TYPES.includes(column.data_type)) return 'integer';
  if (NUMBER_TYPES.includes(column.data_type)) return 'number';
  if (column.data_type === 'boolean') return 'boolean';
  if (column.data_type === 'jsonb' || column.data_type === 'json' || column.data_type === 'ARRAY') return 'json';
  return 'text';
}

/** 열의 한글 라벨 — DDL 주석의 첫 문장(". " 앞). 주석이 없으면 영어 열 이름 */
export function columnLabelOf(column: { name: string; comment: string | null }): string {   // 열 이름 + 주석이면 된다 — 제품 상세의 제원 칸(CatalogSpecView)도 쓴다
  if (column.comment === null || column.comment.trim() === '') return column.name;
  return splitComment(column.comment).label;
}

/** 열의 힌트 — DDL 주석의 첫 문장 뒤(값의 보기 · 단위). 없으면 '' */
export function columnHintOf(column: { comment: string | null }): string {
  if (column.comment === null) return '';
  return splitComment(column.comment).hint;
}

/** 주석 "용량 (GB 단위)" · "SSD 인터페이스. NVMe / SAS / SATA" → 앞 문장과 뒷말 */
function splitComment(comment: string): { label: string; hint: string } {
  const text = comment.trim();
  const cut = text.indexOf('. ');
  if (cut < 0) return { label: text.replace(/\.$/, ''), hint: '' };
  return { label: text.slice(0, cut), hint: text.slice(cut + 2).trim() };
}

/** 글 → 표 이름. 17 밖(옛 non_hw · cable …)이거나 없으면 null */
export function tableOrNull(raw: string | null): ECatalogTable | null {
  const found = E_CATALOG_TABLES.find((table) => table === raw);
  return found === undefined ? null : found;
}

/** 초안 칸 값 → 입력 글. 카탈로그 행 드로어도 쓴다(2026-10-20) */
export function cellTextOf(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** 입력 글 → 보낼 값. 숫자 열은 Number(못 읽으면 글 그대로 — 서버가 막는다), boolean 은 true/false, jsonb 는 JSON(못 읽으면 글) */
export function cellValueOf(text: string, dataType: string): unknown {
  if (NUMBER_TYPES.includes(dataType)) {
    const n = Number(text);
    return Number.isFinite(n) ? n : text;
  }
  if (dataType === 'boolean') return text === 'true' || text === '1' || text === 'y';
  if (dataType === 'jsonb' || dataType === 'json' || dataType === 'ARRAY') {   // ARRAY(pg 배열 열 — fc_protocols) 도 JSON 배열 글로 보낸다. 글이면 서버가 쉼표로 가른다
    try { return JSON.parse(text) as unknown; } catch { return text; }
  }
  return text;
}
